import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronRight, ExternalLink, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Draft = { clientId: string; clientSecret: string; organizationId: string; syncHours: number };

/**
 * Uber / Bolt connection panel.
 *
 * Replaces the CSV round-trip: the fleet pastes the credentials it generates in the platform's
 * own portal (BYO), or — where the platform allows it — clicks through an OAuth consent screen
 * (CONNECT). "Test" proves the credentials before anything is synced, so a fleet never discovers
 * a bad secret hours later in a failed nightly import.
 */
export default function PlatformConnectors() {
  const qc = useQueryClient();
  const t = useT();
  const { can } = useAuth();
  const ro = !can("settings:write");
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [health, setHealth] = useState<Record<string, any>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["platforms"],
    queryFn: async () => (await api.get("/platforms")).data,
    enabled: can("settings:read"),
  });

  useEffect(() => {
    if (!data) return;
    const seed: Record<string, Draft> = {};
    for (const p of data.platforms) {
      seed[p.platform] = {
        clientId: p.clientId || "",
        clientSecret: "",
        organizationId: p.organizationId || "",
        syncHours: p.syncHours ?? 24,
      };
    }
    setDraft(seed);
  }, [data]);

  const save = useMutation({
    mutationFn: ({ key, body }: any) => api.put(`/platforms/${key}`, body),
    onSuccess: (_r, v: any) => {
      qc.invalidateQueries({ queryKey: ["platforms"] });
      toast.success(`${v.key} settings saved`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const test = useMutation({
    mutationFn: async (key: string) => ({ key, res: (await api.post(`/platforms/${key}/test`)).data }),
    onSuccess: ({ key, res }: any) => {
      setHealth((h) => ({ ...h, [key]: res }));
      if (res.online) toast.success(`${key} credentials accepted${res.organizations?.length ? ` · ${res.organizations.length} organisation(s)` : ""}`);
      else toast.error(`${key}: ${res.detail || "connection failed"}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const sync = useMutation({
    mutationFn: async ({ key, hours }: any) => ({ key, res: (await api.post(`/platforms/${key}/sync`, null, { params: { hours } })).data }),
    onSuccess: ({ key }: any) => { qc.invalidateQueries(); toast.success(`${key} payments pulled`); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const connect = useMutation({
    mutationFn: async (key: string) => (await api.get(`/platforms/${key}/authorize`)).data,
    onSuccess: (d: any) => { window.open(d.url, "_blank", "noopener"); toast.success(t("Approve access in the tab that just opened, then press Test")); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!can("settings:read")) return null;
  if (isLoading || !data) return null;

  return (
    <div className="tg-card p-5 space-y-3" data-testid="settings-platforms">
      <div className="flex items-center justify-between">
        <div className="tg-label">{t("Ride platforms · Uber & Bolt")}</div>
        <span className="font-mono text-[10px] text-slate-500">
          {`${data.platforms.filter((p: any) => p.configured).length} / ${data.platforms.length} ${t("connected")}`}
        </span>
      </div>
      <p className="text-xs text-slate-400">
        {t("Connect a platform to pull trips and driver payments straight from its API instead of uploading a CSV every week. Credentials are stored per fleet and are never shown again once saved.")}
      </p>

      <div className="space-y-1.5" data-testid="platform-list">
        {data.platforms.map((p: any) => {
          const key = p.platform;
          const d = draft[key] || { clientId: "", clientSecret: "", organizationId: "", syncHours: 24 };
          const set = (patch: Partial<Draft>) => setDraft({ ...draft, [key]: { ...d, ...patch } });
          const isOpen = open === key;
          const h = health[key];
          return (
            <div key={key} className="border border-[#26334d] rounded-md overflow-hidden">
              <button data-testid={`platform-${key}`} onClick={() => setOpen(isOpen ? null : key)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#141b2a]">
                <ChevronRight className={`h-3.5 w-3.5 text-slate-500 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                <span className="font-mono text-xs text-slate-100">{p.label}</span>
                {p.enabled && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">{t("ENABLED")}</span>}
                <span data-testid={`platform-status-${key}`}
                      className={`font-mono text-[10px] ml-auto ${p.configured ? "text-emerald-400" : "text-slate-600"}`}>
                  {p.configured ? `KEY ${p.clientSecret}` : t("not connected")}
                </span>
              </button>

              {isOpen && (
                <div className="px-3 pb-3 pt-1 space-y-2 bg-[#0d1220]" data-testid={`platform-panel-${key}`}>
                  <div className="text-[11px] text-slate-500">
                    {p.setupHint}
                    <a href={p.portal} target="_blank" rel="noreferrer"
                       className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 ml-1">
                      open portal <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>

                  <label className="block text-xs text-slate-400">{t("Client ID")}
                    <Input data-testid={`platform-client-id-${key}`} disabled={ro}
                           className="mt-1 bg-[#0f141f] border-[#26334d] font-mono text-xs"
                           value={d.clientId} onChange={(e) => set({ clientId: e.target.value })} /></label>

                  <label className="block text-xs text-slate-400">
                    {t("Client secret")} <span className="text-slate-600">{t("(blank keeps the stored one)")}</span>
                    <Input data-testid={`platform-client-secret-${key}`} disabled={ro} type="password"
                           placeholder={p.configured ? p.clientSecret : t("paste the secret")}
                           className="mt-1 bg-[#0f141f] border-[#26334d] font-mono text-xs"
                           value={d.clientSecret} onChange={(e) => set({ clientSecret: e.target.value })} /></label>

                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-xs text-slate-400">{t("Organisation ID")} <span className="text-slate-600">(Uber)</span>
                      <Input data-testid={`platform-org-${key}`} disabled={ro}
                             className="mt-1 bg-[#0f141f] border-[#26334d] font-mono text-xs"
                             value={d.organizationId} onChange={(e) => set({ organizationId: e.target.value })} /></label>
                    <label className="block text-xs text-slate-400">{t("Sync window (hours)")}
                      <Input data-testid={`platform-sync-hours-${key}`} disabled={ro} type="number" min={1} max={2160}
                             className="mt-1 bg-[#0f141f] border-[#26334d] font-mono text-xs"
                             value={d.syncHours} onChange={(e) => set({ syncHours: Number(e.target.value) })} /></label>
                  </div>

                  <div className="text-[11px] text-slate-500">
                    {t("Reads")}: <span className="font-mono text-slate-400">{p.capabilities.join(" · ")}</span> · {t("freshness")} {p.freshness}
                  </div>

                  {!ro && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" data-testid={`platform-save-${key}`} disabled={save.isPending}
                              onClick={() => save.mutate({ key, body: { clientId: d.clientId, clientSecret: d.clientSecret || undefined, organizationId: d.organizationId, syncHours: d.syncHours, enabled: true } })}
                              className="bg-blue-600 hover:bg-blue-500 text-white">{t("Save")}</Button>
                      <Button size="sm" variant="outline" data-testid={`platform-test-${key}`}
                              disabled={test.isPending || !p.configured}
                              onClick={() => test.mutate(key)}
                              className="border-[#26334d] bg-transparent text-slate-200">
                        {test.isPending ? t("Testing…") : t("Test connection")}</Button>
                      <Button size="sm" variant="outline" data-testid={`platform-sync-${key}`}
                              disabled={sync.isPending || !p.configured}
                              onClick={() => sync.mutate({ key, hours: d.syncHours })}
                              className="border-[#26334d] bg-transparent text-slate-200">
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />{sync.isPending ? t("Syncing…") : t("Sync payments now")}</Button>
                      {p.modes.includes("CONNECT") && (
                        <Button size="sm" variant="outline" data-testid={`platform-connect-${key}`}
                                disabled={connect.isPending || !d.clientId}
                                title="Opens the platform's consent screen; requires the redirect URI below to be registered"
                                onClick={() => connect.mutate(key)}
                                className="border-blue-500/40 bg-transparent text-blue-300">{t("Connect with")} {p.label}</Button>
                      )}
                    </div>
                  )}

                  {h && (
                    <div data-testid={`platform-health-${key}`}
                         className={`flex items-start gap-2 text-[11px] rounded-md p-2 border ${h.online ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-amber-500/30 bg-amber-500/5 text-amber-300"}`}>
                      {h.online ? <CheckCircle2 className="h-3.5 w-3.5 mt-px shrink-0" /> : <XCircle className="h-3.5 w-3.5 mt-px shrink-0" />}
                      <span>{h.online ? t("Credentials accepted.") : h.detail}
                        {h.organizations?.length > 0 && (
                          <> Organisations: <span className="font-mono">{h.organizations.map((o: any) => o.id || o.organization_id || o.uuid).filter(Boolean).join(", ") || h.organizations.length}</span></>
                        )}
                        {h.organizationsError && <> Could not list organisations: {h.organizationsError}</>}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-[11px] text-slate-500 border-t border-[#26334d] pt-2 space-y-1">
        <div>{t("Redirect URI to register in the platform console")}: <span className="font-mono text-slate-400 break-all">{data.redirectUri}</span></div>
        <div>{t("Privacy policy URL")}: <span className="font-mono text-slate-400 break-all">{data.privacyPolicyUrl}</span></div>
      </div>
    </div>
  );
}
